import { faListCheck } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { RadioCard } from 'components/Form/RadioCard/RadioCard';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { Alert } from 'components/UI/Alert/Alert';
import { IconCircle } from 'components/UI/IconCircle/IconCircle';
import { PromptModal } from 'components/UI/PromptModal/PromptModal';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getEvaluationServices, getServices } from 'reducers/services/ServicesSelectors';
import { dispatch } from 'store';
import { ReportEvaluationResult } from 'types/Evaluation';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { Service } from 'types/Service';
import {
    navigateToStep,
    removeOnlineBookingBookingItems,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { getOnlineBookingBoardingItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { isOnlineBookingItemEmpty, OnlineBookingStep } from 'types/OnlineBooking';
import {
    formatServicePriceLabel,
    getAvailableServicesForPet,
    isBoardingServiceAvailableForPets,
    isServiceAvailableForPet
} from 'utils/serviceUtils';
import classes from './OnlineBookingBoardingServiceListStep.module.scss';

export const OnlineBookingBoardingServiceListStep: React.FC = React.memo(() => {
    const marketplace = useMarketplace();

    const services = useSelector(getServices);
    const evaluationServices = useSelector(getEvaluationServices);
    const boardingBookingItems = useSelector(getOnlineBookingBoardingItems);

    const [showExitConfirmationModal, setShowExitConfirmationModal] = React.useState(false);
    const [selectedBookingItemUuid, setSelectedBookingItemUuid] = React.useState<string>(
        boardingBookingItems[0].uuid
    );

    const hasBoardingEvaluation = React.useMemo(() => {
        return (
            marketplace.evaluationEnabled &&
            evaluationServices.some(
                service =>
                    service.evaluationMetadata?.businessAreas.includes(
                        MarketplaceServiceCategory.BOARDING
                    )
            )
        );
    }, [evaluationServices, marketplace.evaluationEnabled]);

    const eligiblePets = React.useMemo(() => {
        if (!hasBoardingEvaluation) {
            return boardingBookingItems.filter(item => {
                return services
                    .filter(service => service.businessArea === MarketplaceServiceCategory.BOARDING)
                    .some(service => isServiceAvailableForPet(service, item.pet));
            });
        }

        return boardingBookingItems.filter(
            item => item.pet.evaluationReport?.boardingResult === ReportEvaluationResult.PASS
        );
    }, [boardingBookingItems, hasBoardingEvaluation, services]);

    const nonEligiblePets = React.useMemo(() => {
        if (!hasBoardingEvaluation) {
            return boardingBookingItems.filter(item => {
                return services
                    .filter(service => service.businessArea === MarketplaceServiceCategory.BOARDING)
                    .every(service => !isServiceAvailableForPet(service, item.pet));
            });
        }

        return boardingBookingItems.filter(
            item => item.pet.evaluationReport?.boardingResult === ReportEvaluationResult.FAIL
        );
    }, [boardingBookingItems, hasBoardingEvaluation, services]);

    const petsRequiringEvaluation = React.useMemo(() => {
        if (!hasBoardingEvaluation) return [];

        return boardingBookingItems.filter(
            item =>
                !item.pet.evaluationReport ||
                item.pet.evaluationReport?.boardingResult === ReportEvaluationResult.FAIL
        );
    }, [boardingBookingItems, hasBoardingEvaluation]);

    const selectedBookingItem = React.useMemo(() => {
        return boardingBookingItems.find(item => item.uuid === selectedBookingItemUuid);
    }, [boardingBookingItems, selectedBookingItemUuid]);

    const eligibleBookingItemUuids = React.useMemo(() => {
        return new Set(eligiblePets.map(item => item.uuid));
    }, [eligiblePets]);

    const selectedBookingItemIsEligible = React.useMemo(() => {
        return eligibleBookingItemUuids.has(selectedBookingItemUuid);
    }, [eligibleBookingItemUuids, selectedBookingItemUuid]);

    const selectedPetRequiresEvaluation = React.useMemo(() => {
        return (
            hasBoardingEvaluation &&
            petsRequiringEvaluation.some(item => item.uuid === selectedBookingItemUuid)
        );
    }, [hasBoardingEvaluation, petsRequiringEvaluation, selectedBookingItemUuid]);

    const sharedSelectedService = React.useMemo(() => {
        if (eligiblePets.length === 0) return undefined;

        const selectedServiceId = eligiblePets[0].service?.id;

        if (!selectedServiceId) return undefined;

        if (!eligiblePets.every(item => item.service?.id === selectedServiceId)) {
            return undefined;
        }

        return eligiblePets[0].service;
    }, [eligiblePets]);

    const hasSharedSelectedService = !!sharedSelectedService?.id;

    const isValid = React.useMemo(() => {
        return eligiblePets.length > 0 && hasSharedSelectedService;
    }, [eligiblePets.length, hasSharedSelectedService]);

    const eligibleSelectedPets = React.useMemo(() => {
        return eligiblePets.map(item => item.pet);
    }, [eligiblePets]);

    const sharedAvailableServices = React.useMemo(() => {
        if (!selectedBookingItemIsEligible || !selectedBookingItem?.pet) {
            return [];
        }

        return getAvailableServicesForPet(
            services,
            selectedBookingItem.pet,
            MarketplaceServiceCategory.BOARDING
        ).filter(item => isBoardingServiceAvailableForPets(item.service, eligibleSelectedPets));
    }, [eligibleSelectedPets, selectedBookingItem, selectedBookingItemIsEligible, services]);

    const serviceChangeHandler = (service: Service) => {
        const updatedItems = boardingBookingItems.map(item => ({
            ...item,
            service: eligibleBookingItemUuids.has(item.uuid) ? service : item.service,
            // Reset dates when room changes so min nights is re-validated at DatePicker.
            startDate: undefined,
            endDate: undefined
        }));

        dispatch(updateOnlineBookingBookingItems(updatedItems));
    };

    const backHandler = React.useCallback(() => {
        const emptyBookingItems = boardingBookingItems.filter(isOnlineBookingItemEmpty);

        if (emptyBookingItems.length === boardingBookingItems.length) {
            dispatch(removeOnlineBookingBookingItems(boardingBookingItems.map(item => item.uuid)));

            return dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
        }

        setShowExitConfirmationModal(true);
    }, [boardingBookingItems]);

    const exitConfirmationModalConfirmHandler = React.useCallback(() => {
        dispatch(removeOnlineBookingBookingItems(boardingBookingItems.map(item => item.uuid)));
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, [boardingBookingItems]);

    const submitHandler = () => {
        if (!hasSharedSelectedService) {
            return;
        }

        if (petsRequiringEvaluation.length > 0 || nonEligiblePets.length > 0) {
            const nonEligibleUuids = Array.from(
                new Set([
                    ...petsRequiringEvaluation.map(item => item.uuid),
                    ...nonEligiblePets.map(item => item.uuid)
                ])
            );

            dispatch(removeOnlineBookingBookingItems(nonEligibleUuids));
        }

        return dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1 gap-5">
                <div className={classes.petTabs}>
                    {boardingBookingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            checked={
                                eligibleBookingItemUuids.has(item.uuid)
                                    ? hasSharedSelectedService
                                    : !!item.service?.id
                            }
                            active={selectedBookingItemUuid === item.uuid}
                            onClick={() => setSelectedBookingItemUuid(item.uuid)}
                        />
                    ))}
                </div>

                {selectedBookingItemIsEligible && sharedAvailableServices.length === 0 && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        text="There are no rooms available for all selected pets. Please contact the business for assistance."
                    />
                )}

                {!selectedBookingItemIsEligible && !selectedPetRequiresEvaluation && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        text="There are no services available for your pet. Please contact the business for assistance."
                    />
                )}

                {selectedPetRequiresEvaluation && (
                    <div className="flex flex-1 flex-col items-center justify-center text-center gap-3">
                        <IconCircle size={72} icon={faListCheck} />

                        <h2 className="h3 w-full">This pet needs to pass an evaluation</h2>

                        <p className="text-md mx-auto max-w-sm">
                            Please contact the business to arrange your appointment.
                        </p>
                    </div>
                )}

                {selectedBookingItemIsEligible &&
                    sharedAvailableServices.length > 0 &&
                    !selectedPetRequiresEvaluation && (
                        <>
                            <h1 className="h1 w-full underlined mb-5">Select room</h1>

                            <div className="flex flex-col gap-3">
                                {sharedAvailableServices.map(item => {
                                    const minNights = item.service.boardingMetadata?.minNights ?? 1;
                                    const priceLabel = formatServicePriceLabel(
                                        item.price,
                                        marketplace.onlineBookingPreferences.pricingOption,
                                        '/night'
                                    );
                                    const description =
                                        minNights > 1
                                            ? `${priceLabel} · Minimum ${minNights} nights`
                                            : priceLabel;

                                    return (
                                        <RadioCard
                                            key={item.service.id}
                                            title={item.service.name}
                                            content={item.service.description}
                                            image={item.service.rooms?.[0]?.photos?.[0] ?? null}
                                            description={description}
                                            checked={sharedSelectedService?.id === item.service.id}
                                            onChange={() => serviceChangeHandler(item.service)}
                                        />
                                    );
                                })}
                            </div>
                        </>
                    )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-1">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Continue"
                        disabled={!isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>

            {showExitConfirmationModal && (
                <PromptModal
                    open={true}
                    title="Exit Boarding Services"
                    description="Closing now will discard any unsaved changes. Do you want to exit?"
                    onConfirm={exitConfirmationModalConfirmHandler}
                    onCancel={() => setShowExitConfirmationModal(false)}
                />
            )}
        </TwoColumnsLayout>
    );
});

OnlineBookingBoardingServiceListStep.displayName = 'OnlineBookingBoardingServiceListStep';
